import { useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Iconify from '../iconify';
import { MarketingService } from '../../api/marketing.service';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export default function CustomCampaignsDialog({ open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [campaignData, setCampaignData] = useState({
    name: '',
    type: 'SMS' as 'SMS' | 'Email',
  });

  const [filters, setFilters] = useState({
    ageMin: '',
    ageMax: '',
    gender: '',
    totalSpendMin: '',
    lastVisitAfter: '',
    nhsEligible: false,
    postcode: '',
  });

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const handleNext = () => {
    if (activeStep === 0 && !campaignData.name) {
      enqueueSnackbar('Please enter a campaign name', { variant: 'error' });
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDone = async () => {
    try {
      setLoading(true);
      const marketingService = new MarketingService();

      await marketingService.launchCampaign({
        name: campaignData.name,
        type: campaignData.type,
        filters: {
          ageMin: filters.ageMin ? Number(filters.ageMin) : undefined,
          ageMax: filters.ageMax ? Number(filters.ageMax) : undefined,
          gender: filters.gender || undefined,
          totalSpendMin: filters.totalSpendMin ? Number(filters.totalSpendMin) : undefined,
          lastVisitAfter: filters.lastVisitAfter || undefined,
          nhsEligible: filters.nhsEligible,
          postcode: filters.postcode || undefined,
        },
        template: {
          subject: campaignData.type === 'Email' ? subject : undefined,
          body,
        },
      });

      enqueueSnackbar('Campaign launched successfully!');
      handleCancel();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to launch campaign', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setActiveStep(0);
    setCampaignData({ name: '', type: 'SMS' });
    setFilters({
      ageMin: '',
      ageMax: '',
      gender: '',
      totalSpendMin: '',
      lastVisitAfter: '',
      nhsEligible: false,
      postcode: '',
    });
    setSubject('');
    setBody('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography
          variant="h3"
          component="div"
          sx={{ fontWeight: "600", textAlign: "center", mt: 2 }}
        >
          {activeStep === 0 ? "Identify Audience" : "Create Content"}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {activeStep === 0 ? (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Campaign Details
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <TextField
                  label="Campaign Name"
                  fullWidth
                  value={campaignData.name}
                  onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                  placeholder="e.g. Summer Sale 2024"
                  size="small"
                />
                <ToggleButtonGroup
                  value={campaignData.type}
                  exclusive
                  onChange={(e, val) => val && setCampaignData({ ...campaignData, type: val })}
                  size="small"
                  color="primary"
                >
                  <ToggleButton value="SMS">SMS</ToggleButton>
                  <ToggleButton value="Email">Email</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Targeting Filters
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Min Age"
                  type="number"
                  fullWidth
                  value={filters.ageMin}
                  onChange={(e) => setFilters({ ...filters, ageMin: e.target.value })}
                  size="small"
                />
                <TextField
                  label="Max Age"
                  type="number"
                  fullWidth
                  value={filters.ageMax}
                  onChange={(e) => setFilters({ ...filters, ageMax: e.target.value })}
                  size="small"
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={filters.gender}
                    onChange={(e) => setFilters({ ...filters, gender: (e.target.value as string) })}
                    displayEmpty
                  >
                    <MenuItem value="">Any Gender</MenuItem>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Min Total Spend (£)"
                  type="number"
                  fullWidth
                  value={filters.totalSpendMin}
                  onChange={(e) => setFilters({ ...filters, totalSpendMin: e.target.value })}
                  size="small"
                />
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Postcode Prefix"
                  fullWidth
                  value={filters.postcode}
                  onChange={(e) => setFilters({ ...filters, postcode: e.target.value })}
                  size="small"
                  placeholder="e.g. NW1"
                />
                <TextField
                  label="Last Visit After"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={filters.lastVisitAfter}
                  onChange={(e) => setFilters({ ...filters, lastVisitAfter: e.target.value })}
                  size="small"
                />
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.nhsEligible}
                    onChange={(e) => setFilters({ ...filters, nhsEligible: e.target.checked })}
                  />
                }
                label="NHS Eligible Patients Only"
              />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Message Content ({campaignData.type})
              </Typography>
              {campaignData.type === 'Email' && (
                <TextField
                  fullWidth
                  label="Subject Line"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={{ mb: 2 }}
                  size="small"
                />
              )}
              <TextField
                fullWidth
                multiline
                rows={8}
                label="Message Body"
                placeholder={campaignData.type === 'SMS' ? "Hi {{firstName}}, your order is ready!" : "Hello {{firstName}}, we have a special offer for you!"}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                variant="outlined"
              />
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                Use {"{{firstName}}"} or {"{{lastName}}"} for personalization.
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleCancel} variant="outlined" color="error" disabled={loading}>
          Cancel
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined" disabled={loading}>
            Back
          </Button>
        )}
        {activeStep === 0 ? (
          <Button onClick={handleNext} variant="contained" color="primary">
            Next: Content
          </Button>
        ) : (
          <Button
            onClick={handleDone}
            variant="contained"
            color="success"
            disabled={loading || !body}
            startIcon={loading && <Iconify icon="eos-icons:loading" />}
          >
            {loading ? 'Launching...' : 'Launch Campaign'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}